import React from 'react';
import { StyleSheet } from 'react-native';
import Svg, { ClipPath, Defs, G, LinearGradient, Mask, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';

export type GradientStops = Array<[string, string]>;

type Props = {
	width: number;
	height: number;
	radius: number;
	/** Top-to-bottom stops, as `[offset, colour]`. */
	stops: GradientStops;
	stroke?: string;
	/** A top-to-bottom gradient for the rim instead of the flat `stroke`. */
	strokeStops?: GradientStops;
	strokeWidth?: number;
	/**
	 * An inner glow hugging the rim: `stops` are `[distance in from the rim, opacity]` pairs, in
	 * this plate's units, interpolated between. Drawn without a blur so it renders the same on every
	 * platform and stays on the rim's pixel grid: stacked strokes centred on the rim and clipped to
	 * the plate, each reaching a little deeper and fainter, so no two soft edges ever touch. `fade` scales it from
	 * top to bottom (`[offset, opacity]`), e.g. to hold an additive glow's strength level over a
	 * fill that darkens downwards.
	 */
	innerGlow?: {
		color: string;
		stops: Array<[number, number]>;
		fade?: Array<[string, number]>;
	};
};

/** Depth step between the glow's stacked layers. */
const GLOW_STEP = 0.5;

const glowOpacity = (stops: Array<[number, number]>, distance: number) => {
	for (let i = 1; i < stops.length; i++) {
		const [d0, o0] = stops[i - 1];
		const [d1, o1] = stops[i];
		if (distance <= d1) return o0 + ((o1 - o0) * (distance - d0)) / (d1 - d0);
	}
	return 0;
};

/**
 * A rounded rectangle filled with a vertical gradient and rimmed with a stroke, laid over its
 * parent. React Native views take one flat colour, so the redesign's gradient plates - buttons,
 * panels, cards - are drawn with this instead.
 */
export const GradientPlate = ({
	width,
	height,
	radius,
	stops,
	stroke,
	strokeStops,
	strokeWidth = 0,
	innerGlow,
}: Props) => {
	const fillId = useSvgId('gradientPlate');
	const strokeId = useSvgId('gradientPlateRim');
	const fadeId = useSvgId('gradientPlateGlowFade');
	const maskId = useSvgId('gradientPlateGlowMask');
	const inset = strokeWidth / 2;
	const glowReach = innerGlow ? innerGlow.stops[innerGlow.stops.length - 1][0] : 0;
	const clipId = useSvgId('gradientPlateClip');
	// Layer j covers everything from the rim to `reach`; where layers overlap their opacities
	// compound, so each one gets what takes the stack from the next layer's level to its own target.
	const glowLayers: Array<{ reach: number; opacity: number }> = [];
	if (innerGlow) {
		const levels: Array<{ reach: number; target: number }> = [];
		for (let t = 0; t < glowReach; t += GLOW_STEP) {
			levels.push({
				reach: t + GLOW_STEP,
				target: glowOpacity(innerGlow.stops, t + GLOW_STEP / 2),
			});
		}
		levels.forEach(({ reach, target }, j) => {
			const deeper = j + 1 < levels.length ? levels[j + 1].target : 0;
			const opacity = 1 - (1 - Math.min(target, 0.999)) / (1 - Math.min(deeper, 0.999));
			if (opacity > 0) glowLayers.push({ reach, opacity });
		});
	}
	return (
		<Svg pointerEvents='none' style={StyleSheet.absoluteFill} width={width} height={height}>
			<Defs>
				<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
					{stops.map(([offset, color]) => (
						<Stop key={offset} offset={offset} stopColor={color} />
					))}
				</LinearGradient>
				{strokeStops ? (
					<LinearGradient id={strokeId} x1='0' y1='0' x2='0' y2='1'>
						{strokeStops.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				) : null}
				{innerGlow?.fade ? (
					// The fade masks the finished stack: applied per layer it would compound.
					<>
						<LinearGradient
							id={fadeId}
							gradientUnits='userSpaceOnUse'
							x1={0}
							y1={0}
							x2={0}
							y2={height}
						>
							{innerGlow.fade.map(([offset, opacity]) => (
								<Stop key={offset} offset={offset} stopColor='#FFFFFF' stopOpacity={opacity} />
							))}
						</LinearGradient>
						<Mask id={maskId} maskUnits='userSpaceOnUse' x={0} y={0} width={width} height={height}>
							<Rect x={0} y={0} width={width} height={height} fill={`url(#${fadeId})`} />
						</Mask>
					</>
				) : null}
				{glowLayers.length ? (
					<ClipPath id={clipId}>
						<Rect
							x={strokeWidth}
							y={strokeWidth}
							width={width - strokeWidth * 2}
							height={height - strokeWidth * 2}
							rx={Math.max(0, radius - strokeWidth)}
						/>
					</ClipPath>
				) : null}
			</Defs>
			<Rect
				x={inset}
				y={inset}
				width={width - strokeWidth}
				height={height - strokeWidth}
				rx={Math.max(0, radius - inset)}
				fill={`url(#${fillId})`}
			/>
			{glowLayers.length ? (
				<G clipPath={`url(#${clipId})`} mask={innerGlow!.fade ? `url(#${maskId})` : undefined}>
					{glowLayers.map(layer => (
						<Rect
							key={layer.reach}
							x={strokeWidth}
							y={strokeWidth}
							width={width - strokeWidth * 2}
							height={height - strokeWidth * 2}
							rx={Math.max(0, radius - strokeWidth)}
							fill='none'
							stroke={innerGlow!.color}
							strokeOpacity={layer.opacity}
							strokeWidth={layer.reach * 2}
						/>
					))}
				</G>
			) : null}
			{stroke || strokeStops ? (
				<Rect
					x={inset}
					y={inset}
					width={width - strokeWidth}
					height={height - strokeWidth}
					rx={Math.max(0, radius - inset)}
					fill='none'
					stroke={strokeStops ? `url(#${strokeId})` : stroke}
					strokeWidth={strokeWidth}
				/>
			) : null}
		</Svg>
	);
};

export default GradientPlate;
